import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { IconButton, CircularProgress } from "@mui/material";
import api from "../../utils/api";

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const accent = { from: "#667eea", to: "#764ba2" };
const surface = { page: "#f8fafc", card: "#ffffff", border: "#e2e8f0", muted: "#f8fafc" };

/** Calendar Y-M-D from API (first 10 chars), avoids UTC shift from toISOString(). */
function parseYmdParts(raw) {
  if (raw == null) return null;
  const s = String(raw).trim().slice(0, 10);
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]) - 1;
  const d = Number(m[3]);
  if (mo < 0 || mo > 11 || d < 1 || d > 31) return null;
  return { y, mo, d };
}

function ymdKey(parts) {
  return `${parts.y}-${String(parts.mo + 1).padStart(2, "0")}-${String(parts.d).padStart(2, "0")}`;
}

function normalizeRecords(response) {
  const body = response?.data;
  const nested = body?.data;
  let records =
    nested?.records ??
    body?.records ??
    (Array.isArray(nested) ? nested : null) ??
    (Array.isArray(body) ? body : null) ??
    body?.data ??
    [];
  if (!Array.isArray(records) && nested && typeof nested === "object") {
    if (Array.isArray(nested.items)) records = nested.items;
    else if (Array.isArray(nested.data)) records = nested.data;
  }
  return Array.isArray(records) ? records : [];
}

const styles = {
  page: {
    minHeight: "100vh",
    width: "100%",
    boxSizing: "border-box",
    backgroundColor: surface.page,
    backgroundImage: `
      radial-gradient(ellipse 900px 520px at 0% -8%, rgba(37, 99, 235, 0.10), transparent 55%),
      radial-gradient(ellipse 760px 420px at 100% 0%, rgba(118, 75, 162, 0.10), transparent 50%),
      linear-gradient(180deg, #f8fafc 0%, #f1f5f9 45%, #f8fafc 100%)
    `,
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    padding: "clamp(12px, 2.5vw, 28px)",
    paddingBottom: 48,
  },
  shell: {
    maxWidth: 1180,
    margin: "0 auto",
  },
  topBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 16,
    marginBottom: 20,
    flexWrap: "wrap",
  },
  backBtn: {
    width: 44,
    height: 44,
    borderRadius: 12,
    border: `1px solid ${surface.border}`,
    backgroundColor: "rgba(255,255,255,0.92)",
    boxShadow: "0 1px 3px rgba(15, 23, 42, 0.08)",
    color: "#475569",
  },
  hero: {
    textAlign: "center",
    marginBottom: 28,
    padding: "0 8px 8px",
  },
  badge: {
    display: "inline-block",
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    color: "#5b21b6",
    backgroundColor: "rgba(102, 126, 234, 0.12)",
    border: "1px solid rgba(102, 126, 234, 0.28)",
    padding: "6px 14px",
    borderRadius: 999,
    marginBottom: 14,
  },
  title: {
    margin: 0,
    fontSize: "clamp(1.55rem, 4vw, 2.05rem)",
    fontWeight: 800,
    letterSpacing: "-0.03em",
    lineHeight: 1.15,
    color: "#0f172a",
  },
  titleGradient: {
    backgroundImage: `linear-gradient(115deg, ${accent.from} 0%, ${accent.to} 55%, #5b21b6 100%)`,
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
  },
  subtitle: {
    margin: "12px auto 0",
    maxWidth: 520,
    fontSize: 15,
    lineHeight: 1.55,
    color: "#64748b",
    fontWeight: 400,
  },
  statsRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 12,
    justifyContent: "center",
    marginBottom: 28,
  },
  statChip: {
    backgroundColor: "rgba(255,255,255,0.9)",
    border: `1px solid ${surface.border}`,
    borderRadius: 12,
    padding: "12px 18px",
    minWidth: 120,
    textAlign: "center",
    boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
  },
  statValue: {
    fontSize: 22,
    fontWeight: 800,
    color: "#0f172a",
    fontVariantNumeric: "tabular-nums",
    letterSpacing: "-0.02em",
  },
  statLabel: {
    fontSize: 11,
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    color: "#64748b",
    marginTop: 4,
  },
  monthCard: {
    backgroundColor: surface.card,
    borderRadius: 16,
    border: `1px solid ${surface.border}`,
    boxShadow: "0 4px 6px -1px rgba(15, 23, 42, 0.06), 0 10px 22px -10px rgba(15, 23, 42, 0.08)",
    marginBottom: 14,
    overflow: "hidden",
  },
  monthHeader: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 16,
    padding: "16px 20px",
    border: "none",
    cursor: "pointer",
    textAlign: "left",
    background: `linear-gradient(135deg, ${accent.from} 0%, ${accent.to} 100%)`,
    color: "#fff",
    fontFamily: "inherit",
    fontSize: 16,
    fontWeight: 700,
    letterSpacing: "-0.02em",
    transition: "filter 0.2s, box-shadow 0.2s",
  },
  monthHeaderMeta: {
    fontSize: 13,
    fontWeight: 500,
    opacity: 0.92,
  },
  chevron: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "transform 0.25s ease",
  },
  dayBlock: {
    borderBottom: `1px solid ${surface.border}`,
    backgroundColor: surface.muted,
  },
  dayBlockLast: {
    borderBottom: "none",
  },
  dayLabelBar: {
    padding: "12px 18px",
    background: "linear-gradient(90deg, #f1f5f9 0%, #f8fafc 100%)",
    borderBottom: `1px solid ${surface.border}`,
  },
  dayLabelText: {
    fontSize: 14,
    fontWeight: 700,
    color: "#334155",
    letterSpacing: "-0.01em",
  },
  dayLabelSub: {
    fontSize: 12,
    fontWeight: 500,
    color: "#64748b",
    marginTop: 2,
  },
  tableWrap: {
    overflowX: "auto",
    backgroundColor: "#fff",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: 13,
    minWidth: 640,
  },
  th: {
    textAlign: "left",
    padding: "11px 14px",
    background: "linear-gradient(180deg, #334155 0%, #1e293b 100%)",
    fontWeight: 600,
    fontSize: 11,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: "#f1f5f9",
    borderBottom: "1px solid rgba(255,255,255,0.08)",
  },
  td: {
    padding: "11px 14px",
    borderBottom: `1px solid ${surface.border}`,
    color: "#334155",
    verticalAlign: "top",
  },
  trAlt: {
    backgroundColor: "#f8fafc",
  },
  emptyCell: {
    padding: "20px 14px",
    textAlign: "center",
    color: "#94a3b8",
    fontSize: 14,
    fontStyle: "italic",
  },
  messageCard: {
    textAlign: "center",
    padding: "36px 28px",
    borderRadius: 16,
    border: `1px dashed ${surface.border}`,
    backgroundColor: "rgba(255,255,255,0.75)",
    color: "#475569",
    fontSize: 15,
    lineHeight: 1.6,
    maxWidth: 520,
    margin: "0 auto",
  },
  errorCard: {
    border: "1px solid #fecaca",
    backgroundColor: "#fef2f2",
    color: "#991b1b",
  },
  loadingCard: {
    maxWidth: 400,
    margin: "80px auto",
    padding: "48px 32px",
    borderRadius: 16,
    backgroundColor: "rgba(255,255,255,0.95)",
    border: `1px solid ${surface.border}`,
    boxShadow: "0 12px 40px -12px rgba(15, 23, 42, 0.12)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 20,
  },
  loadingText: {
    fontSize: 15,
    fontWeight: 600,
    color: "#475569",
  },
  ticketPill: {
    display: "inline-block",
    fontSize: 11,
    fontWeight: 700,
    padding: "3px 8px",
    borderRadius: 6,
    backgroundColor: "rgba(102, 126, 234, 0.14)",
    color: "#5b21b6",
  },
};

const EmployeeDataAllList = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedMonth, setExpandedMonth] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchEmployeeData = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await api.get("/employee-data/all", {
          params: { limit: 10000 },
        });
        setData(normalizeRecords(response));
      } catch (err) {
        setError("We could not load employee data. Check your connection and try again.");
        console.error("Error fetching employee data:", err);
        setData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchEmployeeData();
  }, []);

  const filteredAndGrouped = useMemo(() => {
    const start = new Date(2020, 0, 1);
    const end = new Date(2035, 11, 31, 23, 59, 59);

    const filtered = data.filter((item) => {
      const parts = parseYmdParts(item.date);
      if (!parts) return false;
      const t = new Date(parts.y, parts.mo, parts.d).getTime();
      return t >= start.getTime() && t <= end.getTime();
    });

    const byMonth = {};
    filtered.forEach((item) => {
      const parts = parseYmdParts(item.date);
      if (!parts) return;
      const { y: year, mo: month } = parts;
      const key = `${year}-${month}`;
      if (!byMonth[key]) {
        byMonth[key] = { year, month, monthName: MONTH_NAMES[month], items: [] };
      }
      byMonth[key].items.push(item);
    });

    const keys = Object.keys(byMonth).sort((a, b) => {
      const [yA, mA] = a.split("-").map(Number);
      const [yB, mB] = b.split("-").map(Number);
      return yA !== yB ? yA - yB : mA - mB;
    });
    return keys.map((k) => byMonth[k]);
  }, [data]);

  const stats = useMemo(() => {
    let lineCount = 0;
    filteredAndGrouped.forEach((m) => {
      m.items.forEach((item) => {
        lineCount += Array.isArray(item.entries) ? item.entries.length : 0;
      });
    });
    return {
      months: filteredAndGrouped.length,
      records: filteredAndGrouped.reduce((s, m) => s + m.items.length, 0),
      lines: lineCount,
    };
  }, [filteredAndGrouped]);

  const getDaysInMonth = (year, month) => {
    const d = new Date(year, month + 1, 0);
    return d.getDate();
  };

  const getDataForDate = (monthData, day) => {
    const { year, month } = monthData;
    const target = ymdKey({ y: year, mo: month, d: day });
    return monthData.items.filter((item) => {
      const parts = parseYmdParts(item.date);
      if (!parts) return false;
      return ymdKey(parts) === target;
    });
  };

  const entryCountForMonth = (monthData) =>
    monthData.items.reduce(
      (sum, item) => sum + (Array.isArray(item.entries) ? item.entries.length : 0),
      0
    );

  if (loading) {
    return (
      <div style={styles.page}>
        <div style={styles.shell}>
          <div style={styles.loadingCard} role="status">
            <CircularProgress size={48} sx={{ color: "#667eea" }} />
            <div style={styles.loadingText}>Loading employee tickets…</div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.page}>
        <div style={styles.shell}>
          <div style={styles.topBar}>
            <IconButton
              onClick={() => navigate(-1)}
              aria-label="Go back"
              sx={styles.backBtn}
            >
              <ArrowBackIcon />
            </IconButton>
          </div>
          <div style={{ ...styles.messageCard, ...styles.errorCard }}>{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.shell}>
        <div style={styles.topBar}>
          <IconButton
            onClick={() => navigate(-1)}
            aria-label="Go back"
            sx={styles.backBtn}
          >
            <ArrowBackIcon />
          </IconButton>
        </div>

        <header style={styles.hero}>
          <span style={styles.badge}>Tickets</span>
          <h1 style={styles.title}>
            <span style={styles.titleGradient}>Employee data by month</span>
          </h1>
          <p style={styles.subtitle}>
            Browse consolidated daily tickets by month and day. Open a month to inspect each calendar day.
          </p>
        </header>

        {filteredAndGrouped.length > 0 && (
          <div style={styles.statsRow}>
            <div style={styles.statChip}>
              <div style={styles.statValue}>{stats.months}</div>
              <div style={styles.statLabel}>Months</div>
            </div>
            <div style={styles.statChip}>
              <div style={styles.statValue}>{stats.records}</div>
              <div style={styles.statLabel}>Day records</div>
            </div>
            <div style={styles.statChip}>
              <div style={styles.statValue}>{stats.lines}</div>
              <div style={styles.statLabel}>Ticket lines</div>
            </div>
          </div>
        )}

        {filteredAndGrouped.length === 0 ? (
          <div style={styles.messageCard}>
            No data in the 2020–2035 window, or dates could not be parsed. Confirm the API returns records with a{" "}
            <code style={{ fontSize: 13, color: "#5b21b6" }}>date</code> field (YYYY-MM-DD).
          </div>
        ) : (
          filteredAndGrouped.map((monthData) => {
            const key = `${monthData.year}-${monthData.month}`;
            const isExpanded = expandedMonth === key;
            const daysInMonth = getDaysInMonth(monthData.year, monthData.month);
            const lines = entryCountForMonth(monthData);

            return (
              <div key={key} style={styles.monthCard}>
                <button
                  type="button"
                  style={styles.monthHeader}
                  onClick={() => setExpandedMonth((prev) => (prev === key ? null : key))}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.filter = "brightness(1.05)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.filter = "none";
                  }}
                >
                  <span>
                    {monthData.monthName} {monthData.year}
                    <span style={styles.monthHeaderMeta}>
                      {" · "}
                      {monthData.items.length} day record{monthData.items.length !== 1 ? "s" : ""}
                      {lines > 0 ? ` · ${lines} ticket line${lines !== 1 ? "s" : ""}` : ""}
                    </span>
                  </span>
                  <span
                    style={{
                      ...styles.chevron,
                      transform: isExpanded ? "rotate(180deg)" : "rotate(0)",
                    }}
                  >
                    <ExpandMoreIcon sx={{ fontSize: 28 }} />
                  </span>
                </button>

                {isExpanded && (
                  <div>
                    {Array.from({ length: daysInMonth }, (_, i) => i + 1).map((day, dayIdx) => {
                      const dayData = getDataForDate(monthData, day);
                      const dateObj = new Date(monthData.year, monthData.month, day);
                      const dateLabel = dateObj.toLocaleDateString("en-GB", {
                        weekday: "long",
                        day: "numeric",
                        month: "long",
                      });
                      const isLast = dayIdx === daysInMonth - 1;

                      return (
                        <div
                          key={day}
                          style={{
                            ...styles.dayBlock,
                            ...(isLast ? styles.dayBlockLast : {}),
                          }}
                        >
                          <div style={styles.dayLabelBar}>
                            <div style={styles.dayLabelText}>
                              {String(day).padStart(2, "0")} · {dateLabel}
                            </div>
                            <div style={styles.dayLabelSub}>
                              {dayData.length === 0
                                ? "No submissions"
                                : `${dayData.reduce((n, i) => n + (i.entries?.length || 0), 0)} row(s)`}
                            </div>
                          </div>
                          <div style={styles.tableWrap}>
                            <table style={styles.table}>
                              <thead>
                                <tr>
                                  <th style={styles.th}>Employee</th>
                                  <th style={styles.th}>Ticket</th>
                                  <th style={styles.th}>Hours</th>
                                  <th style={styles.th}>Work</th>
                                  <th style={styles.th}>Description</th>
                                </tr>
                              </thead>
                              <tbody>
                                {dayData.length === 0 ? (
                                  <tr>
                                    <td colSpan={5} style={{ ...styles.td, ...styles.emptyCell }}>
                                      No entries for this date
                                    </td>
                                  </tr>
                                ) : (
                                  dayData.flatMap((item) =>
                                    (item.entries || []).map((entry, idx) => (
                                      <tr
                                        key={`${item._id || item.employeeId}-${day}-${idx}`}
                                        style={idx % 2 === 1 ? styles.trAlt : undefined}
                                      >
                                        <td style={styles.td}>
                                          <strong style={{ fontWeight: 600, color: "#0f172a" }}>
                                            {item.employeeId}
                                          </strong>
                                        </td>
                                        <td style={styles.td}>
                                          {entry.ticketNo ? (
                                            <span style={styles.ticketPill}>{entry.ticketNo}</span>
                                          ) : (
                                            <span style={{ color: "#94a3b8" }}>—</span>
                                          )}
                                        </td>
                                        <td style={styles.td}>{entry.noOfHours ?? "—"}</td>
                                        <td style={styles.td}>{entry.work || "—"}</td>
                                        <td style={{ ...styles.td, maxWidth: 360 }}>
                                          {(entry.description || "—").slice(0, 120)}
                                          {(entry.description || "").length > 120 ? "…" : ""}
                                        </td>
                                      </tr>
                                    ))
                                  )
                                )}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default EmployeeDataAllList;
